package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import dev.lingvo.site.components.studio.fonts.FontLoadProgress
import dev.lingvo.site.components.studio.fonts.FontDownloadProgress
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.components.studio.fonts.FontLinks
import dev.lingvo.site.components.studio.fonts.FontGate
import dev.lingvo.site.components.studio.fonts.projectFontRequests
import dev.lingvo.site.ds.ControlHeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.components.SectionNavItemStyle
import dev.lingvo.site.components.SectionNavStyle
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.nav.UrlState
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Text

/** Real links preserve Back and open-in-new-tab, while platform changes replace the current URL. */
@Composable
fun ScreenshotsAreaNav(
    slug: String, tab: ScreenshotsTab, params: ScreenshotsAreaState,
    onPlatformChange: (ScreenshotsAreaState) -> Unit = {}, modifier: Modifier = Modifier,
) {
    val project = liveProjectRoute(slug)?.project
    var fontProgress by remember(slug, tab) { mutableStateOf<FontLoadProgress?>(null) }
    LaunchedEffect(slug, tab, project?.sourceLocale, project?.locales) {
        if (tab == ScreenshotsTab.DESIGN || tab == ScreenshotsTab.REVIEW) {
            val locales = project?.let { listOf(it.sourceLocale) + it.locales }.orEmpty()
            val requests = projectFontRequests(locales)
            requests.forEach { FontLinks.add(it.family) }
            try { FontGate.load(requests) { fontProgress = it } } finally { fontProgress = null }
        }
    }
    val palette = ColorMode.current.toSitePalette()
    val navLabel = t("app.projects.slug.screenshots.screenshots_area_nav.screenshots", "Screenshots")
    Column(modifier.gap(Space.MD).styleModifier { property("min-width", "0"); property("max-width", "100%") }) {
        Nav(SectionNavStyle.toModifier().toAttrs { attr("aria-label", navLabel) }) {
            Row(Modifier.gap(Space.XS).styleModifier { property("flex-wrap", "wrap") }) {
                ScreenshotsTab.entries.forEach { destination ->
                    val label = when (destination) {
                        ScreenshotsTab.PLAN -> t("app.projects.slug.screenshots.screenshots_area_nav.plan", "Plan")
                        ScreenshotsTab.DESIGN -> t("app.projects.slug.screenshots.screenshots_area_nav.design", "Design")
                        ScreenshotsTab.REVIEW -> t("app.projects.slug.screenshots.screenshots_area_nav.review", "Review")
                        ScreenshotsTab.UPDATES -> t("app.projects.slug.screenshots.screenshots_area_nav.updates", "Updates")
                    }
                    val active = destination == tab
                    val linkStyle = SectionNavItemStyle.toModifier().styleModifier { property("min-height", ControlHeight.MD.toString()); property("box-sizing", "border-box"); property("display", "inline-flex"); property("align-items", "center") }.then(
                        if (active) Modifier.backgroundColor(palette.accentSubtle).color(palette.accent).styleModifier {
                            property("text-decoration", "underline")
                            property("text-underline-offset", "4px")
                        } else Modifier,
                    )
                    A(href = screenshotsPath(slug, destination, params), attrs = linkStyle.toAttrs {
                        if (destination == tab) attr("aria-current", "page")
                    }) { Text(label) }
                }
            }
        }
        if (tab == ScreenshotsTab.DESIGN) fontProgress?.let { FontDownloadProgress(it) }
        LSegmented(
            options = listOf(LSegmentedOption(StorePlatform.APP_STORE, "App Store"), LSegmentedOption(StorePlatform.GOOGLE_PLAY, "Google Play")),
            selected = params.platform,
            onSelected = { platform ->
                if (platform != params.platform) {
                    val updated = params.copy(platform = platform, device = null, cell = null, slot = null, layer = null)
                    UrlState.replace(updated.toParams(tab))
                    onPlatformChange(updated)
                }
            },
            label = t("app.projects.slug.screenshots.screenshots_area_nav.screenshot_platform", "Screenshot platform"),
        )
    }
}
